import type { Cell } from "@station-keeping/sim";

/**
 * Vue isométrique 3:2, un peu plongeante (plus qu'un 2:1 classique : les bâtiments masquent moins les cases
 * de derrière). L'unité du monde est le pixel d'art : une case est un losange de 72 × 48 pixels, agrandi
 * ensuite par la caméra. Le noyau, case (0, 0), est à l'origine ; l'axe x de la grille descend vers la
 * droite de l'écran, l'axe y vers la gauche.
 */
export const TILE_W = 72;
export const TILE_H = 48;
const HALF_W = TILE_W / 2;
const HALF_H = TILE_H / 2;

export interface Point {
  x: number;
  y: number;
}

/** Centre au sol d'une case (coordonnées de grille éventuellement fractionnaires). */
export function toIso(gx: number, gy: number): Point {
  return { x: (gx - gy) * HALF_W, y: (gx + gy) * HALF_H };
}

/** Position de grille (fractionnaire) d'un point du sol. */
export function fromIso(x: number, y: number): Point {
  return { x: (x / HALF_W + y / HALF_H) / 2, y: (y / HALF_H - x / HALF_W) / 2 };
}

/** Case du sol sous un point : dans le repère de la grille, chaque losange devient un carré unité. */
export function groundCell(x: number, y: number): Cell {
  const grid = fromIso(x, y);
  // `+ 0` : pas de « -0 » dans les cases.
  return { x: Math.round(grid.x) + 0, y: Math.round(grid.y) + 0 };
}

/** Ordre d'affichage : ce qui est plus bas à l'écran (plus près) passe devant. */
export function depthOf(gx: number, gy: number): number {
  return (gx + gy) * 100 + (gx - gy) * 0.01;
}

/** Un bloc posé sur une case, haut de `height` pixels au-dessus du centre du sol (pour la sélection). */
export interface Block {
  cell: Cell;
  height: number;
  /** Demi-largeur de l'empreinte (un peu moins qu'une case : les blocs ne se touchent pas). */
  halfWidth?: number;
  /** Silhouette exacte (sprite), en coordonnées relatives au centre du sol ; à défaut, l'hexagone du bloc. */
  contains?: (local: Point) => boolean;
}

/** Le point est-il dans la silhouette du bloc (hexagone : losange du dessus, faces, losange du sol) ? */
export function insideBlock(point: Point, block: Block): boolean {
  const center = toIso(block.cell.x, block.cell.y);
  if (block.contains) return block.contains({ x: point.x - center.x, y: point.y - center.y });
  const hw = block.halfWidth ?? HALF_W - 6;
  const hh = (hw * TILE_H) / TILE_W;
  const dx = Math.abs(point.x - center.x);
  if (dx > hw) return false;
  // Bas : le losange du sol ; haut : le losange du toit, `height` plus haut que le centre du sol.
  const slope = TILE_H / TILE_W;
  const bottom = center.y + hh - dx * slope;
  const top = center.y - block.height + dx * slope;
  return point.y <= bottom && point.y >= top;
}

/**
 * Case visée par un point du monde : d'abord le bloc le plus en avant dont la silhouette le contient (on
 * touche un module par son volume, pas par sa case au sol), sinon la case du sol.
 */
export function pickCell(point: Point, blocks: readonly Block[]): Cell {
  let best: Block | null = null;
  for (const block of blocks) {
    if (!insideBlock(point, block)) continue;
    if (!best || depthOf(block.cell.x, block.cell.y) > depthOf(best.cell.x, best.cell.y)) best = block;
  }
  return best ? { ...best.cell } : groundCell(point.x, point.y);
}

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Hauteur réservée au-dessus de la rangée du fond, pour les modules les plus hauts. */
export const HEADROOM = 58;

/** Emprise à l'écran des cases jusqu'à `extent` anneaux (marge comprise), modules du fond inclus. */
export function stationBounds(extent: number): Bounds {
  const halfWidth = extent * TILE_W + HALF_W;
  const halfHeight = extent * TILE_H + HALF_H;
  return { x: -halfWidth, y: -halfHeight - HEADROOM, width: halfWidth * 2, height: halfHeight * 2 + HEADROOM };
}
